import { formatCurrency, formatDateTime } from '@eriko/shared-types';
import { Landmark } from 'lucide-react';

interface Ref {
  name?: string;
  value?: string;
}

interface MetaData {
  CreateTime?: string;
  LastUpdatedTime?: string;
}

export function AccountDetailReport({ data }: { data: Record<string, unknown> }) {
  const name = String(data.FullyQualifiedName ?? data.Name ?? '—');
  const accountType = data.AccountType ? String(data.AccountType) : '';
  const accountSubType = data.AccountSubType ? String(data.AccountSubType) : '';
  const classification = data.Classification ? String(data.Classification) : '';
  const description = data.Description ? String(data.Description) : '';
  const provider = data.provider ? String(data.provider) : '';
  const externalId = String(data.externalId ?? data.Id ?? '—');
  const active = data.Active === true;
  const isSubAccount = data.SubAccount === true;

  const currency = data.CurrencyRef as Ref | undefined;
  const currencyName = currency?.name ?? 'United States Dollar';
  const currencyCode = currency?.value ?? '';

  const balance = Number(data.CurrentBalance ?? 0);
  const balanceWithSub = Number(data.CurrentBalanceWithSubAccounts ?? balance);
  const hasSubBalance = balanceWithSub !== balance;
  const isNegative = balance < 0;

  const meta = data.MetaData as MetaData | undefined;
  const createTime = meta?.CreateTime ?? '';
  const lastUpdated = meta?.LastUpdatedTime ?? '';

  const detailRows: Array<[string, string]> = [
    ['Type', accountType || '—'],
    ['Subtype', accountSubType || '—'],
    ['Classification', classification || '—'],
    ['Currency', currencyCode ? `${currencyName} (${currencyCode})` : currencyName],
    ['Account ID', externalId],
  ];
  if (provider) detailRows.push(['Source', provider]);

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 px-8 py-7">
      {/* Header: account identity + balance */}
      <div className="flex items-start justify-between gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
            <Landmark className="h-3.5 w-3.5" strokeWidth={1.5} />
            {isSubAccount ? 'Sub-account' : 'Account'}
          </div>
          <div
            className="text-[28px] leading-tight text-surface-900"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400 }}
          >
            {name}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-surface-600">
              {active ? 'Active' : 'Inactive'}
            </span>
            {classification && (
              <span className="inline-flex items-center rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-surface-600">
                {classification}
              </span>
            )}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
            Current Balance
          </div>
          <div
            className={`font-mono text-[28px] leading-none tabular-nums ${
              isNegative ? 'text-neg' : 'text-surface-900'
            }`}
          >
            {formatCurrency(balance, 2)}
          </div>
          {hasSubBalance && (
            <div className="mt-1.5 text-[13px] text-surface-500">
              {formatCurrency(balanceWithSub, 2)} with sub-accounts
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="my-7 border-t border-surface-200" />

      {/* Detail grid */}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-5">
        {detailRows.map(([label, value]) => (
          <div key={label}>
            <dt className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
              {label}
            </dt>
            <dd className="text-sm text-surface-900">{value}</dd>
          </div>
        ))}
      </dl>

      {/* Description */}
      {description && (
        <>
          <div className="my-7 border-t border-surface-200" />
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
              Description
            </div>
            <div className="text-sm leading-relaxed text-surface-700">{description}</div>
          </div>
        </>
      )}

      {/* Footer: timestamps */}
      {(createTime || lastUpdated) && (
        <>
          <div className="mt-7 mb-5 border-t border-surface-200" />
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            {createTime && (
              <div>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                  Created
                </div>
                <div className="text-sm text-surface-900">{formatDateTime(createTime)}</div>
              </div>
            )}
            {lastUpdated && (
              <div>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                  Last Updated
                </div>
                <div className="text-sm text-surface-900">{formatDateTime(lastUpdated)}</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
